import React, { useEffect, useRef } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import clsx from 'clsx';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import * as validationRules from '@msc-b2c/shared-utils';
import {
  convertPathToDotNotation,
  createObjectFromDotNotation,
  getCountryCodeFromMetaTag,
  getPageLocale,
  telInputLocaleSearch,
} from '@msc-b2c/shared-utils';
import { ValidationRulesContainer } from '@msc-b2c/interfaces/types';

type PhoneInputProps = {
  label: string;
  placeholder: string;
  name: string;
  prefixName: string;
  index: number;
  control: Control<any>;
  errors: FieldErrors;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  isLowerLabel?: boolean;
  validationRulesContainer?: ValidationRulesContainer[];
  additionalValidationObj?: any;
  register: UseFormRegister<any>;
  trigger: any;
  context?: any;
};

export const PhoneInput = ({
  label,
  placeholder,
  name,
  prefixName,
  index,
  control,
  errors,
  clearErrors,
  setValue,
  disabled,
  isLowerLabel,
  validationRulesContainer,
  additionalValidationObj,
  register,
  trigger,
  context,
}: PhoneInputProps) => {
  const isError = convertPathToDotNotation(errors, name);
  const isErrorClass = isError ? 'error-input' : '';
  const isDisabled = disabled ? 'disabled-input' : '';
  const labelClass = isLowerLabel ? 'cmp-input__lowerlabel' : 'cmp-input__upperlabel';

  return (
    <div className={`text ${labelClass} ${isErrorClass} ${isDisabled}`}>
      <div className="cmp-form-text tel">
        {!isLowerLabel && (
          <label
            className={clsx('cmp-label', isErrorClass)}
            htmlFor={`${name}-${index}`}
          >
            {label}
          </label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <IntlTelInputWrapper
              field={field}
              name={name}
              prefixName={prefixName}
              index={index}
              clearErrors={clearErrors}
              setValue={setValue} // Pass setValue to wrapper
              isError={!!isError}
              disabled={disabled}
              label={label}
              placeholder={placeholder}
              errors={errors}
              validationRulesContainer={validationRulesContainer}
              additionalValidationObj={additionalValidationObj}
              register={register}
              trigger={trigger}
              context={context}
            />
          )}
        />
        {isError && (
          <div className="error-list-validation">
            <div>{isError.message?.toString()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

type IntlTelInputWrapperProps = {
  field: any;
  name: string;
  prefixName: string;
  index: number;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>; // Add setValue prop
  isError: boolean;
  disabled?: boolean;
  label: string;
  placeholder: string;
  errors: FieldErrors;
  validationRulesContainer?: ValidationRulesContainer[];
  additionalValidationObj?: any;
  register: UseFormRegister<any>;
  trigger: any;
  context?: any;
};

const IntlTelInputWrapper = ({
  field,
  name,
  prefixName,
  index,
  clearErrors,
  setValue,
  isError,
  disabled,
  placeholder,
  errors,
  validationRulesContainer,
  additionalValidationObj,
  register,
  trigger,
  context,
}: IntlTelInputWrapperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<any>(null);

  useEffect(() => {
    if (inputRef.current) {
      itiRef.current = intlTelInput(inputRef.current, {
        strictMode: true,
        initialCountry: getCountryCodeFromMetaTag(),
        i18n: telInputLocaleSearch[getPageLocale()],
        separateDialCode: true,
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.5.0/build/js/utils.js',
      });
      (inputRef.current as any)._itiInitialized = true; // Prevent double initialization

      inputRef.current.addEventListener('countrychange', handleCountryChange);

      if (field.value) {
        itiRef.current.setNumber(field.value);
      } else {
        const initialNumber = itiRef.current.getNumber();
        field.onChange(initialNumber);
      }

      const initialDialCode = itiRef.current.getSelectedCountryData().dialCode;
      setValue(prefixName, initialDialCode);
    }

    return () => {
      if (itiRef.current) {
        itiRef.current.destroy();
      }
    };
  }, [disabled]);

  const handleCountryChange = () => {
    const number = itiRef.current.getNumber();
    field.onChange(number);

    const dialCode = itiRef.current.getSelectedCountryData().dialCode;
    setValue(prefixName, dialCode);

    trigger(name);
  };

  const handleBlur = () => {
    const number = itiRef.current.getNumber();
    field.onChange(number);
    field.onBlur();
    trigger(name);
    if (context) {
      context.setGuestFormData({
        ...context.guestFormData,
        ...createObjectFromDotNotation(name, number),
      });
    }
  };

  const handleChange = () => {
    const number = itiRef.current.getNumber();
    field.onChange(number);
    trigger(name);
  };

  const handleInput = () => {
    const number = itiRef.current.getNumber();
    field.onChange(number);
  };

  const validateNumber = () => {
    if (itiRef.current) {
      let errorMessage: string | boolean = true;
      const number = itiRef.current.getNumber();
      validationRulesContainer?.some((rule: ValidationRulesContainer) => {
        if (rule?.validationRules === 'vr-mandatory-field') {
          if (!validationRules.vrMandatoryField(number)) {
            errorMessage = rule.validationRuleErrorMessage;
            return true;
          }
        } else if (rule?.validationRules === 'vr-phone-number') {
          if (!validationRules.vrPhoneNumber(itiRef.current)) {
            errorMessage = rule.validationRuleErrorMessage;
            return true;
          }
        } else if (rule?.validationRules === 'vr-emergency-phone-number') {
          if (additionalValidationObj?.passengerData?.passengers) {
            let passengersData;
            passengersData = additionalValidationObj?.passengerData?.passengers;
            //check if exist a passenger with phone that is equals to the emergency contact phone
            passengersData = passengersData?.filter((passenger: { phone: string }) => {
              return passenger.phone === itiRef.current.getNumber();
            });
            if (passengersData.length > 0) {
              errorMessage = rule.validationRuleErrorMessage;
              return true;
            }
          }
        } else {
          clearErrors(name);
          return true;
        }
        return false;
      });
      return errorMessage;
    }
  };

  return (
    <input
      {...register(name, {
        validate: () => {
          return validateNumber();
        },
      })}
      ref={inputRef}
      name={name}
      id={`${name}-${index}`}
      type="tel"
      className={clsx('cmp-form-text__text tel-input-not-editorial iti__tel-input', {
        'error-input': convertPathToDotNotation(errors, name),
      })}
      inputMode="numeric" // Show numeric keyboard on mobile devices
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onInput={handleInput}
      onFocus={() => clearErrors(name)}
      aria-invalid={isError ? 'true' : 'false'}
      defaultValue={field.value}
    />
  );
};
